import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { ClauseOutcome, ReversalTier } from '../../engine/types'
import { getContractOutput } from '../../engine/contractReducer'
import { useContractGameStore } from '../../store/useContractGameStore'

type Props = {
  onConfirm: () => void
}

function useCountUp(target: number, duration = 1000) {
  const [value, setValue] = useState(0)
  const rafRef = useRef<number>(0)
  useEffect(() => {
    const start = performance.now()
    const step = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      setValue(Math.round(eased * target))
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step)
      }
    }
    rafRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(rafRef.current)
  }, [target, duration])
  return value
}

const TIER_COLORS: Record<ReversalTier, string> = {
  full: '#C9A96E',
  success: '#7DAF8B',
  failure: '#D4849A',
}

export function GameResultModal({ onConfirm }: Props) {
  const { t } = useTranslation()
  const state = useContractGameStore()
  const output = getContractOutput(state)
  const displayScore = useCountUp(output.score, 1000)

  const successes = output.clauseOutcomes.filter(o => o.result === 'success' || o.result === 'full')
  const failures = output.clauseOutcomes.filter(o => o.result === 'failure')

  const tierLabel = (outcome: ClauseOutcome): string => t(`clause.${outcome.result}`)

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ animation: 'fade-in 200ms ease-out' }}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      />

      {/* Modal card */}
      <div
        className="relative rounded-xl overflow-hidden flex flex-col"
        style={{
          width: 420,
          maxHeight: '80vh',
          background: 'linear-gradient(160deg, #1A2F25 0%, #152820 100%)',
          border: '2px solid rgba(201,169,110,0.5)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          animation: 'scale-in 250ms ease-out',
        }}
      >
        {/* Header */}
        <div
          className="px-5 py-4 text-center shrink-0"
          style={{
            background: 'linear-gradient(135deg, #4A3520, #5A4030, #4A3520)',
            borderBottom: '1px solid rgba(201,169,110,0.3)',
          }}
        >
          <div className="text-lg font-bold font-heading" style={{ color: '#E6DCC8' }}>
            {state.victory ? t('summary.victory') : t('summary.defeat')}
          </div>
          <div
            className="text-[11px] font-bold tracking-[3px] uppercase mt-0.5"
            style={{ color: state.victory ? '#7DAF8B' : '#D4849A' }}
          >
            {state.victory ? t('summary.victoryEn') : t('summary.defeatEn')}
          </div>
          <div
            className="text-3xl font-black font-mono mt-2"
            style={{ color: '#C9A96E', textShadow: '0 0 12px rgba(201,169,110,0.3)' }}
          >
            {t('summary.score', { score: displayScore })}
          </div>
        </div>

        {/* Clause outcomes */}
        <div className="overflow-y-auto flex-1 px-4 py-3">
          <div className="flex flex-col gap-1.5">
            {output.clauseOutcomes.map(outcome => {
              const color = TIER_COLORS[outcome.result]
              return (
                <div key={outcome.clauseId} className="flex items-center gap-2 py-2 px-3 rounded-md" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                  <div className="flex-1 overflow-hidden flex gap-2 items-center">
                    <span className="text-[11px] font-mono shrink-0" style={{ color: 'rgba(180,220,190,0.5)' }}>{outcome.clauseId}</span>
                    <span className="text-[12px] overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: 'rgba(230,220,200,0.7)' }}>
                      {outcome.clauseText.slice(0, 40)}...
                    </span>
                  </div>
                  <span className="text-[11px] font-semibold shrink-0" style={{ color }}>
                    {tierLabel(outcome)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Stats row */}
        <div
          className="flex justify-center gap-6 px-4 py-3 shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(180,220,190,0.4)' }}>{t('summary.statsReversed')}</div>
            <div className="text-xl font-bold font-mono" style={{ color: '#7DAF8B' }}>{successes.length}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(180,220,190,0.4)' }}>{t('summary.statsFailed')}</div>
            <div className="text-xl font-bold font-mono" style={{ color: '#D4849A' }}>{failures.length}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(180,220,190,0.4)' }}>{t('summary.statsScore')}</div>
            <div className="text-xl font-bold font-mono" style={{ color: '#C9A96E' }}>{state.runningScore}</div>
          </div>
        </div>

        {/* Confirm button */}
        <div className="px-4 pb-4 shrink-0">
          <button
            type="button"
            className="w-full py-3 border-none rounded-lg text-white text-base font-bold cursor-pointer tracking-wider"
            style={{
              background: 'linear-gradient(135deg, #C9A96E, #A88B55)',
              boxShadow: '0 4px 16px rgba(201,169,110,0.3)',
              animation: 'fade-in 400ms ease-out 800ms both',
            }}
            onClick={onConfirm}
          >
            {t('summary.confirm', '确认')}
          </button>
        </div>
      </div>
    </div>
  )
}
